/**
 * GUIDE_CONFIGS — 8 group-guide configs consumed by GuidePage.
 *
 * Copy is plain Thai for non-HR users; technical terms kept in parentheses for
 * those who know them (per pariwat 2026-06-02). Org / people / positions /
 * competency are READ-ONLY here (PayPilot SoT, ADR 0003) — the steps describe
 * viewing, not creating.
 */
import type { GuideConfig } from './guide-types';

export const GUIDE_CONFIGS: Record<string, GuideConfig> = {
  'org-guide': {
    icon: 'network',
    color: 'indigo',
    title: 'คู่มือ โครงสร้างองค์กร',
    subtitle: 'รากฐานของทุกอย่างในแอป',
    intro:
      'ที่นี่เก็บผังบริษัท แผนก ตำแหน่ง หน้าที่แต่ละตำแหน่ง (JD) ทักษะที่ต้องมี และค่านิยมบริษัท — เป็นรากฐานให้ระบบประเมินและระบบพัฒนาคนทำงาน · ข้อมูลทั้งหมดตั้งค่าที่ระบบเงินเดือน (PayPilot) แล้วมาแสดงที่นี่ให้อ่าน',
    steps: [
      { title: 'ดูผังบริษัทและแผนก', body: 'เปิดแท็บ "ผังองค์กร" · ดูว่าบริษัทมีแผนกอะไร ใครเป็นหัวหน้า · ถ้าต้องแก้ ไปแก้ที่ PayPilot แล้วกดดึงข้อมูลล่าสุด' },
      { title: 'ดูหน้าที่แต่ละตำแหน่ง (JD)', body: 'แท็บ "หน้าที่รายตำแหน่ง" · ดูว่าแต่ละตำแหน่งทำอะไร ระดับไหน ขอบเขตงานแค่ไหน' },
      { title: 'ดูทักษะที่ตำแหน่งต้องมี', body: 'แท็บ "ทักษะประจำตำแหน่ง" · ดูว่าตำแหน่งนั้นต้องมีทักษะอะไรบ้าง (competency) — ใช้เป็นเกณฑ์ตั้งต้นตอนประเมินและวางแผนพัฒนา' },
    ],
    related: [
      { label: 'พนักงาน', route: 'employees', icon: 'users' },
      { label: 'ประเมินผล', route: 'evaluation', icon: 'clipboard-check' },
    ],
    examples: [
      { title: 'อยากรู้ว่าตำแหน่งนี้ต้องเก่งอะไร', scenario: 'เปิดหน้าตำแหน่ง · ดูทักษะที่กำหนดไว้ · ใช้เป็นเกณฑ์ว่าจะประเมินเรื่องอะไรบ้าง' },
      { title: 'บริษัทเพิ่งปรับโครงสร้างใหม่', scenario: 'แก้แผนก/ตำแหน่งที่ PayPilot · กลับมากด "ดึงข้อมูลล่าสุด" · ผังที่นี่อัปเดตตามทันที' },
    ],
    ctaButtons: [
      { label: 'ดูผังองค์กร', icon: 'network', route: 'org', primary: true },
    ],
  },

  'people-guide': {
    icon: 'users',
    color: 'emerald',
    title: 'คู่มือ พนักงาน',
    subtitle: 'รายชื่อคนทั้งบริษัท',
    intro:
      'รายชื่อพนักงานทั้งบริษัท (ดึงมาจากระบบเงินเดือน PayPilot) · ใช้ติดตามช่วงทดลองงาน ดูศักยภาพรายคน และวางเส้นทางเติบโต — เป็นศูนย์กลางที่ระบบประเมินและพัฒนาคนใช้ร่วมกัน',
    steps: [
      { title: 'ดูรายชื่อพนักงาน', body: 'เปิด "รายชื่อ" · รายชื่อมาจาก PayPilot อัตโนมัติ · ถ้าเพิ่งเพิ่มคนใหม่ที่ PayPilot กด "ดึงข้อมูลล่าสุด"' },
      { title: 'ติดตามช่วงทดลองงาน (90 วัน)', body: 'แท็บ "ทดลองงาน" · ระบบเตือนหัวหน้าให้ประเมินทุก 30/60/90 วัน · แล้วตัดสิน ผ่าน / ต่อเวลา / ไม่ผ่าน' },
      { title: 'ดูศักยภาพรายคน (9 ช่อง)', body: 'แท็บ "9 ช่อง · เส้นทางเติบโต" · ดูภาพ ผลงาน × ศักยภาพ ของแต่ละคนทุก 6 เดือน · วางแผนคนสืบทอดตำแหน่ง (succession)' },
    ],
    related: [
      { label: 'โครงสร้างองค์กร', route: 'org', icon: 'network' },
      { label: 'ประเมินผล', route: 'evaluation', icon: 'clipboard-check' },
      { label: 'พัฒนาตัวเอง', route: 'dev-plans', icon: 'rocket' },
    ],
    examples: [
      { title: 'รับคนใหม่เข้างาน', scenario: 'เพิ่มชื่อที่ PayPilot · Eval สร้างช่วงทดลองงาน 90 วันให้อัตโนมัติ · แจ้งหัวหน้าให้ประเมินตอนใกล้ครบกำหนด' },
      { title: 'ทบทวนคนกลางปี', scenario: 'เปิด 9 ช่อง · กรองตามแผนก · เห็นคนเก่งที่ต้องรักษาไว้ และคนที่ต้องช่วยพัฒนา · วางแผนได้ทันที' },
    ],
    ctaButtons: [
      { label: 'ไปดูรายชื่อพนักงาน', icon: 'users', route: 'employees', primary: true },
    ],
  },

  'evaluation-guide': {
    icon: 'clipboard-check',
    color: 'fuchsia',
    title: 'คู่มือ ประเมินผล',
    subtitle: 'เลือกวิธีที่เหมาะ · เปิดเฉพาะที่ใช้',
    intro:
      'รวมวิธีประเมินพนักงาน 20 แบบ — ตั้งแต่วัดผลตามเป้าหมาย (KPI / OKR) ประเมินรอบด้านจากหลายคน (360°) ไปจนถึงจัดอันดับ · ไม่ต้องใช้ทั้งหมด เปิดเฉพาะที่บริษัทใช้จริง · จับมารวมเป็นชุด แล้วผู้ประเมินกรอกได้เลย',
    steps: [
      { title: 'เลือกวิธีที่เหมาะ', body: 'เปิด "เครื่องมือประเมิน" · ดู 20 แบบ · เปิดเฉพาะที่จะใช้ (3-5 แบบก็พอ) · มีผู้ช่วย AI คอยแนะนำให้' },
      { title: 'รวมเป็นชุดประเมินแล้วกำหนดให้พนักงาน', body: 'รวมวิธีที่เปิดไว้เป็นชุด (package) ตามตำแหน่ง/ระดับ · ใส่น้ำหนักคะแนน · กำหนดให้กลุ่มพนักงานเป้าหมาย' },
      { title: 'ผู้ประเมินกรอกแบบฟอร์ม', body: 'หัวหน้าเห็นแบบฟอร์มในกล่องงาน · กรอกตามแบบ · ระบบดึงข้อมูลจากงานประจำวันมากรอกให้ก่อนบางส่วน' },
    ],
    related: [
      { label: 'ศูนย์รวมงาน (Work Hub)', route: 'tasks', icon: 'kanban-square' },
      { label: 'บันทึกผลงาน', route: 'capture-catalog', icon: 'clipboard-edit' },
      { label: 'พัฒนาตัวเอง', route: 'dev-plans', icon: 'rocket' },
    ],
    examples: [
      { title: 'บริษัทเพิ่งเริ่มประเมินครั้งแรก', scenario: 'ให้ผู้ช่วย AI แนะนำ · เปิดแค่ KPI + 9 ช่อง + คุยตัวต่อตัว (1:1) · พอแล้วสำหรับปีแรก · ค่อยเพิ่มทีหลัง' },
      { title: 'บริษัทใช้เป้าหมาย (OKR) + ประเมินรอบด้าน (360°)', scenario: 'เปิดแค่ 2 วิธี · รวมเป็นชุดเดียว · พนักงานเห็นเฉพาะแบบฟอร์มที่เกี่ยวกับตัวเอง' },
    ],
    ctaButtons: [
      { label: 'ไปเลือกวิธีประเมิน', icon: 'target', route: 'evaluation', primary: true },
      { label: 'ดูคู่มือ 20 แบบ', icon: 'book-open', route: 'assessment-knowledge' },
    ],
  },

  'work-hub-guide': {
    icon: 'kanban-square',
    color: 'teal',
    title: 'คู่มือ ศูนย์รวมงาน (Work Hub)',
    subtitle: 'สั่งงาน · ติดตามเป้า · วัดผลรายคน',
    intro:
      'เครื่องมือสำหรับสั่งงาน ติดตามเป้าหมาย และวัดผลรายคน — ทุกส่วนเก็บข้อมูลส่งเข้าระบบประเมินให้อัตโนมัติ · เลือกเปิดเฉพาะที่บริษัทใช้จริง ไม่ต้องเปิดทั้งหมด',
    steps: [
      { title: 'เปิดเฉพาะเครื่องมือที่ใช้', body: 'แท็บ "เลือกเครื่องมือ" · เปิดที่จะใช้ (มอบหมายงาน, เป้าหมาย OKR, ตัวชี้วัด KPI, จัดอันดับ ฯลฯ) · ถ้าวิธีประเมินที่เลือกไว้ต้องใช้ ระบบจะเปิดให้เอง' },
      { title: 'ใช้งานจริงทุกวัน', body: 'พนักงานและหัวหน้าเข้ามาทำงาน · กรอกความคืบหน้า · อัปเดตคะแนน · ระบบเก็บประวัติทั้งหมดให้' },
      { title: 'ส่งเข้าระบบประเมินอัตโนมัติ', body: 'พอถึงรอบประเมิน · ระบบดึงข้อมูลที่เก็บไว้มากรอกคะแนนให้ผู้ประเมินก่อน · ไม่ต้องนึกย้อนหลัง' },
    ],
    related: [
      { label: 'ประเมินผล', route: 'evaluation', icon: 'clipboard-check' },
      { label: 'บันทึกผลงาน', route: 'capture-catalog', icon: 'clipboard-edit' },
      { label: 'พนักงาน', route: 'employees', icon: 'users' },
    ],
    examples: [
      { title: 'บริษัทเริ่มใช้เป้าหมาย (OKR)', scenario: 'เปิดเครื่องมือ "เป้าหมาย" · ตั้งเป้าระดับบริษัท · กระจายลงทีมและรายคน · ติดตามทุกไตรมาส · ส่งเข้าประเมินตอนจบรอบ' },
      { title: 'ทีมขายจัดอันดับทุกเดือน', scenario: 'เปิดเครื่องมือ "จัดอันดับ" · ทำทุกเดือน · ระบบเรียงลำดับตามยอดขาย · ส่งเข้าประเมินรายไตรมาส' },
    ],
    ctaButtons: [
      { label: 'ไปดูกล่องงาน', icon: 'inbox', route: 'tasks', primary: true },
      { label: 'เลือกเครื่องมือ', icon: 'toggle-right', route: 'workhub-catalog' },
    ],
  },

  'my-dev-guide': {
    icon: 'rocket',
    color: 'violet',
    title: 'คู่มือ พัฒนาตัวเอง',
    subtitle: 'แผนพัฒนารายคน · ขออนุมัติ · ติดตามผล',
    intro:
      'สร้างแผนพัฒนาตัวเอง เลือกหลักสูตร ขออนุมัติจากหัวหน้า ติดตามความคืบหน้า และเก็บใบรับรอง — รวมทุกอย่างไว้ที่เดียว · มี AI ช่วยแนะนำหลักสูตรที่ตรงกับจุดที่ยังต้องพัฒนาจากผลประเมิน',
    steps: [
      { title: 'สร้างแผน', body: 'เปิด "แผนพัฒนา" → "สร้างแผน" · เริ่มจากแบบสำเร็จรูป หรือให้ AI ช่วยร่างจากผลประเมินล่าสุด' },
      { title: 'ส่งให้หัวหน้าอนุมัติ', body: 'กรอกเป้าหมาย · เลือกคอร์สหรือพี่เลี้ยง (mentor) · กดส่งให้หัวหน้าและ HR ตรวจ · ปรับตามที่เขาแนะนำ' },
      { title: 'ทำตามแผน', body: 'เข้าเรียน · บันทึกผล · เก็บใบรับรอง · ระบบติดตามและเตือนทุกเดือน · อัปเดตความคืบหน้าตอนคุยกับหัวหน้า' },
    ],
    related: [
      { label: 'ประเมินผล', route: 'evaluation', icon: 'clipboard-check' },
      { label: 'ดูแลการพัฒนาคน (L&D)', route: 'lnd-overview', icon: 'line-chart' },
      { label: 'บันทึกผลงาน', route: 'capture-catalog', icon: 'clipboard-edit' },
    ],
    examples: [
      { title: 'หลังประเมินรอบครึ่งปี', scenario: 'AI ดูว่าทักษะไหนคะแนนยังต่ำ · แนะนำคอร์ส 2-3 ตัว · สร้างเป็นแผนพัฒนา 6 เดือนพร้อมส่งอนุมัติ' },
      { title: 'อยากเปลี่ยนสายงาน', scenario: 'เลือกเส้นทางอาชีพที่อยากไป · ระบบบอกว่าต้องเพิ่มทักษะอะไรบ้าง · ร่างแผนพัฒนา 12 เดือนให้' },
    ],
    ctaButtons: [
      { label: 'สร้างแผนใหม่', icon: 'plus', route: 'dev-plan-builder', primary: true },
      { label: 'เลือกแบบสำเร็จรูป', icon: 'package', route: 'dev-templates' },
    ],
  },

  'lnd-guide': {
    icon: 'line-chart',
    color: 'sky',
    title: 'คู่มือ ดูแลการพัฒนาคนทั้งบริษัท (L&D)',
    subtitle: 'สำหรับเจ้าของ/HR ดูภาพรวมทั้งบริษัท',
    intro:
      'สำหรับเจ้าของและ HR — ดูภาพรวมการพัฒนาคนทั้งบริษัท อนุมัติแผนพัฒนา จัดการผู้จัดอบรมและคอร์ส ดูว่าทักษะส่วนไหนของบริษัทยังขาด และติดตามงบกับความคุ้มค่า (ROI) · เห็นทุกอย่างในที่เดียว ไม่ต้องเปิด Excel หลายไฟล์',
    steps: [
      { title: 'ดูภาพรวมและความคุ้มค่า', body: 'แท็บ "ภาพรวม + ความคุ้มค่า" · เห็นงบเทียบกับผลลัพธ์ · ทักษะที่ปิดช่องว่างได้แล้ว · จำนวนคนเข้าอบรม · ต้นทุนต่อหัว' },
      { title: 'อนุมัติแผนของพนักงาน', body: 'แท็บ "แผน + คน" · ตรวจแผนพัฒนาที่ขออนุมัติ · อนุมัติทั้งกองหรือทีละคน · ส่งกลับให้แก้ได้' },
      { title: 'จัดการผู้จัดอบรมและงบ', body: 'แท็บ "ผู้ให้บริการ" · ลงทะเบียนผู้จัดอบรม · ทำสัญญา · ตัดงบรายเดือน/รายปี' },
    ],
    related: [
      { label: 'พัฒนาตัวเอง', route: 'dev-plans', icon: 'rocket' },
      { label: 'ประเมินผล', route: 'evaluation', icon: 'clipboard-check' },
    ],
    examples: [
      { title: 'ทำรายงานให้ผู้บริหารรายไตรมาส', scenario: 'บันทึกหน้าภาพรวม + ภาพทักษะที่ขาด + งบที่ใช้ไป เป็น PDF · ส่งบอร์ดได้เลย' },
      { title: 'วางแผนพัฒนาคนปีหน้า', scenario: 'ดูว่าทักษะส่วนไหนของแต่ละแผนกยังขาด · จัดลำดับว่าจะอบรมอะไรก่อน · ตั้งงบรายแผนก · แบ่งงบให้หัวหน้าแต่ละคน' },
    ],
    ctaButtons: [
      { label: 'ไปดูภาพรวม + ความคุ้มค่า', icon: 'line-chart', route: 'lnd-overview', primary: true },
      { label: 'จัดการแผน', icon: 'list-checks', route: 'lnd-plans' },
    ],
  },

  'data-capture-guide': {
    icon: 'clipboard-edit',
    color: 'rose',
    title: 'คู่มือ บันทึกข้อมูลผลการทำงาน',
    subtitle: 'บันทึกตอนเกิดเหตุ · ใช้ตอนประเมิน',
    intro:
      'หัวหน้าและ HR บันทึกพฤติกรรม เหตุการณ์เด่น การคุยตัวต่อตัว (1:1) งานที่ผิดหรือต้องแก้ซ้ำ และผลแบบทดสอบ — บันทึกระหว่างปีได้เลย ไม่ต้องรอรอบประเมินใหญ่แล้วค่อยมานึกย้อนหลัง · ตอนประเมินระบบจะดึงมากรอกคะแนนให้',
    steps: [
      { title: 'เปิดเฉพาะสิ่งที่ต้องบันทึก', body: 'แท็บ "เลือกเครื่องมือ" · เปิดเฉพาะที่ตรงกับสิ่งที่จะบันทึก (พฤติกรรม / เหตุการณ์ / คุยตัวต่อตัว / งานผิดพลาด)' },
      { title: 'บันทึกทันทีที่เกิดเหตุ', body: 'พอเห็นพฤติกรรมเด่นหรือปัญหา · กรอกในแอปได้เลย ไม่ถึงนาที · ถ่ายรูปเอกสารแนบได้ด้วย' },
      { title: 'ใช้ตอนประเมิน', body: 'ระบบรวมประวัติทั้งปีเป็นไทม์ไลน์ให้ผู้ประเมิน · กรอกคะแนนทักษะให้ก่อน · ไม่ต้องนึกย้อนหลังหรือลำเอียง' },
    ],
    related: [
      { label: 'ประเมินผล', route: 'evaluation', icon: 'clipboard-check' },
      { label: 'ศูนย์รวมงาน (Work Hub)', route: 'tasks', icon: 'kanban-square' },
      { label: 'พนักงาน', route: 'employees', icon: 'users' },
    ],
    examples: [
      { title: 'หัวหน้าจดพฤติกรรมเด่น', scenario: 'ลูกน้องช่วยสอนงานทีมใหม่ได้ดีมาก · บันทึกไว้ตอนนั้นเลย · พอประเมินครึ่งปี ระบบหยิบขึ้นมาเตือน · ให้คะแนนเรื่องการทำงานเป็นทีมได้แม่นยำ' },
      { title: 'คุยตัวต่อตัวรายเดือน (1:1)', scenario: 'เปิดเครื่องมือ "คุยตัวต่อตัว" · บันทึกหัวข้อที่คุย · อารมณ์/ความรู้สึก · สิ่งที่ต้องทำต่อ · ระบบติดตามว่าทำเสร็จไหม' },
    ],
    ctaButtons: [
      { label: 'เลือกเครื่องมือ', icon: 'toggle-right', route: 'capture-catalog', primary: true },
      { label: 'เริ่มบันทึกพฤติกรรม', icon: 'edit-3', route: 'observations' },
    ],
  },
};
